import React, { useCallback, useState } from 'react';
import { Button, Form, Modal, ModalProps } from 'react-bootstrap';
import { createUseStyles } from 'react-jss';

import { AiProject } from '@/lib/models';
import { aiProjectService } from '@/lib/services';
import { useHandleError } from '@/hooks';
import { LoadingButton, Select } from '@/components';

const AiTextInputModalStyles = createUseStyles({
	dialog: {
		width: '90%',
		height: '100%',
		maxWidth: 620,
		margin: '0 auto',
		'& .modal-content': {
			maxHeight: '90vh',
		},
		'& .hackett-modal__body': {
			overflowY: 'auto',
		},
	},
});

interface AiAddToProjectModalProps extends ModalProps {
	aiConversationId: string;
	onSubmit(aiProject: AiProject): void;
	isLoading?: boolean;
}

export const AiAddToProjectModal = ({
	aiConversationId,
	onSubmit,
	isLoading = false,
	...props
}: AiAddToProjectModalProps) => {
	const classes = AiTextInputModalStyles();
	const handleError = useHandleError();
	const [aiProjects, setAiProjects] = useState<AiProject[]>([]);
	const [projectsLoading, setProjectsLoading] = useState(false);
	const [selectedAiProjectId, setSelectedAiProjectId] = useState('');

	const fetchAiProjects = useCallback(async () => {
		setProjectsLoading(true);

		try {
			const response = await aiProjectService.getProjects().fetch();
			setAiProjects(response.aiProjects);
		} catch (error) {
			handleError(error);
		} finally {
			setProjectsLoading(false);
		}
	}, [handleError]);

	const handleOnEntering = useCallback(() => {
		setSelectedAiProjectId('');
		fetchAiProjects();
	}, [fetchAiProjects]);

	const handleFormSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		setProjectsLoading(true);

		try {
			const response = await aiProjectService
				.addConversationToProject(selectedAiProjectId, { aiConversationId })
				.fetch();

			onSubmit(response.aiProject);
		} catch (error) {
			handleError(error);
		} finally {
			setProjectsLoading(false);
		}
	};

	return (
		<Modal dialogClassName={classes.dialog} centered onEntering={handleOnEntering} {...props}>
			<Modal.Header closeButton>
				<Modal.Title>Add to Project</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<Form onSubmit={handleFormSubmit}>
					<fieldset disabled={projectsLoading || isLoading}>
						<Form.Label>Select Project</Form.Label>
						<Select
							className="mb-8"
							value={selectedAiProjectId}
							onChange={({ currentTarget }) => {
								setSelectedAiProjectId(currentTarget.value);
							}}
						>
							<option value="" disabled>
								Select project...
							</option>
							{aiProjects.map((aiProject) => (
								<option key={aiProject.aiProjectId} value={aiProject.aiProjectId}>
									{aiProject.title}
								</option>
							))}
						</Select>
						<div className="d-flex align-items-center">
							<Button className="mr-3" variant="outline-primary" type="button" onClick={props.onHide}>
								Cancel
							</Button>
							<LoadingButton isLoading={projectsLoading || isLoading} variant="primary" type="submit">
								Add
							</LoadingButton>
						</div>
					</fieldset>
				</Form>
			</Modal.Body>
		</Modal>
	);
};
